/* ==========================================================
   I FEEL LIKE — Drop Room motion (GSAP + ScrollTrigger + SplitText + Lenis)
   Révélations au défilement, parallaxe, ticker qui accélère avec le scroll.
   Tout le contenu reste visible sans JavaScript et quand l'utilisateur
   préfère réduire les animations.
   ========================================================== */
(() => {
  const root = document.documentElement;
  const release = () => root.classList.remove('dr-motion-pending');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || !window.gsap || !window.ScrollTrigger) { release(); return; }

  const { gsap, ScrollTrigger } = window;
  gsap.registerPlugin(ScrollTrigger);
  const hasSplit = !!window.SplitText;
  if (hasSplit) gsap.registerPlugin(window.SplitText);
  root.classList.add('dr-has-motion');

  const fine = window.matchMedia('(pointer: fine)').matches;
  const desktop = window.matchMedia('(min-width: 990px)').matches;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const EASE = 'expo.out';

  /* ---------- Défilement fluide (souris / trackpad uniquement) ---------- */
  let lenis = null;
  if (window.Lenis && fine) {
    lenis = new window.Lenis({
      duration: 1.1,
      easing: (t) => 1 - Math.pow(1 - t, 4),
      smoothWheel: true,
      prevent: (node) => !!(node.closest && node.closest('dialog, .cart-drawer, .menu-drawer, .header__drawer, [data-lenis-prevent], .search-modal, .quick-add-modal')),
    });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
    root.style.scrollBehavior = 'auto';
    // Les tiroirs (panier, menu) bloquent le scroll de la page : Lenis se met en pause avec eux.
    const syncLock = () => {
      const locked = getComputedStyle(root).overflow === 'hidden' || getComputedStyle(document.body).overflow === 'hidden' || root.hasAttribute('scroll-lock') || document.body.hasAttribute('scroll-lock') || !!$('dialog[open]');
      if (locked) lenis.stop(); else lenis.start();
    };
    new MutationObserver(syncLock).observe(root, { attributes: true, attributeFilter: ['style', 'class', 'scroll-lock'] });
    new MutationObserver(syncLock).observe(document.body, { attributes: true, attributeFilter: ['style', 'class', 'scroll-lock'] });
    // Ouverture / fermeture des tiroirs et fenêtres (<dialog open>, <details open>) n'importe où dans la page.
    new MutationObserver(syncLock).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'scroll-lock'] });
    document.addEventListener('toggle', syncLock, true);
    document.addEventListener('close', syncLock, true);
    // Filet de sécurité : si Lenis est resté en pause sans raison, la molette le relance.
    window.addEventListener('wheel', () => { if (lenis.isStopped) syncLock(); }, { passive: true });
    $$('a[href^="#"]').forEach((a) => {
      a.addEventListener('click', (e) => {
        const id = a.getAttribute('href');
        const target = id.length > 1 && $(id);
        if (!target) return;
        e.preventDefault();
        lenis.scrollTo(target, { offset: -80, duration: 1.3 });
      });
    });
  }

  /* ---------- Barre de progression rouge ---------- */
  const bar = document.createElement('div');
  bar.className = 'dr-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);
  gsap.to(bar, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });

  /* ---------- Titres découpés (re-split automatique au redimensionnement) ---------- */
  const splitReveal = (el, type, vars, trigger) => {
    if (!el) return;
    if (!hasSplit) {
      gsap.from(el, { y: 40, opacity: 0, duration: 1, ease: EASE, scrollTrigger: trigger });
      return;
    }
    window.SplitText.create(el, {
      type,
      mask: type.includes('words') ? 'words' : 'lines',
      linesClass: 'dr-split-line',
      autoSplit: true,
      onSplit(self) {
        const parts = type.includes('words') ? self.words : self.lines;
        return gsap.from(parts, Object.assign({ yPercent: 110, duration: 1.1, ease: EASE, stagger: 0.08 }, vars, trigger ? { scrollTrigger: trigger } : {}));
      },
    });
  };

  /* ---------- Héros de l'accueil ---------- */
  const hero = $('.dr-hero');
  if (hero) {
    splitReveal($('.dr-hero__title', hero), 'lines', { duration: 1.25, stagger: 0.1, delay: 0.05 });
    gsap.from($$('.dr-kicker, .dr-hero__sub, .dr-hero__ctas', hero), { y: 26, opacity: 0, duration: 1, ease: EASE, stagger: 0.08, delay: 0.35 });
    const tiles = $$('.dr-wall__tile', hero);
    gsap.from(tiles, { clipPath: 'inset(100% 0% 0% 0%)', duration: 1.3, ease: 'expo.inOut', stagger: 0.09, delay: 0.15 });
    gsap.from($$('.dr-wall__img', hero), { scale: 1.18, duration: 1.8, ease: EASE, stagger: 0.09, delay: 0.15 });
    gsap.from($$('.dr-wall__label', hero), { yPercent: 100, duration: 0.9, ease: EASE, stagger: 0.09, delay: 0.9 });
    if (desktop) {
      tiles.forEach((t, i) => {
        gsap.to(t, { y: i % 2 ? -60 : -20, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.6 } });
      });
    }
  }

  /* ---------- Ticker : accélère et s'incline avec la vitesse de défilement ---------- */
  const track = $('.dr-ticker__track');
  if (track) {
    const anim = track.getAnimations ? track.getAnimations()[0] : null;
    const skewTo = gsap.quickTo(track, 'skewX', { duration: 0.4, ease: 'power3' });
    let decay;
    ScrollTrigger.create({
      onUpdate(self) {
        const v = self.getVelocity();
        const speed = gsap.utils.clamp(1, 7, 1 + Math.abs(v) / 400);
        if (anim) anim.playbackRate = speed * (v < 0 ? -1 : 1) || 1;
        skewTo(gsap.utils.clamp(-8, 8, v / -300));
        clearTimeout(decay);
        decay = setTimeout(() => { if (anim) anim.playbackRate = 1; skewTo(0); }, 180);
      },
    });
  }

  /* ---------- Titres de section : mot par mot ---------- */
  $$('.dr-head h2').forEach((h) => splitReveal(h, 'words', { stagger: 0.05, duration: 1 }, { trigger: h, start: 'top 88%' }));
  const colTitle = $('.dr-col__head h1');
  if (colTitle) splitReveal(colTitle, 'lines', { duration: 1.2, stagger: 0.1 });
  gsap.from($$('.dr-col__top .dr-kicker, .dr-col__desc, .dr-col__bar'), { y: 24, opacity: 0, duration: 0.9, ease: EASE, stagger: 0.07, delay: 0.25 });

  /* ---------- Cartes produit en cascade ---------- */
  gsap.set('.dr-grid > li', { y: 60, opacity: 0 });
  ScrollTrigger.batch('.dr-grid > li', {
    start: 'top 92%',
    onEnter: (batch) => gsap.to(batch, { y: 0, opacity: 1, duration: 1, ease: EASE, stagger: 0.07, overwrite: true }),
    onEnterBack: (batch) => gsap.to(batch, { y: 0, opacity: 1, duration: 0.6, ease: EASE, overwrite: true }),
  });
  // Sécurité : tout ce qui est déjà au-dessus de l'écran est affiché d'office.
  ScrollTrigger.addEventListener('refreshInit', () => {
    $$('.dr-grid > li').forEach((li) => { if (li.getBoundingClientRect().bottom < 0) gsap.set(li, { y: 0, opacity: 1 }); });
  });

  /* ---------- Univers : tuiles qui montent, vignettes qui s'éventaillent ---------- */
  const uni = $('.dr-univers__grid');
  if (uni) {
    gsap.from(uni.children, { y: 90, opacity: 0, duration: 1.1, ease: EASE, stagger: 0.1, scrollTrigger: { trigger: uni, start: 'top 85%' } });
    $$('.dr-uni__stack', uni).forEach((stack) => {
      gsap.from(stack.children, { y: 70, rotation: 0, opacity: 0, duration: 1, ease: 'back.out(1.6)', stagger: 0.08, scrollTrigger: { trigger: stack, start: 'top 95%' } });
    });
  }

  /* ---------- Scène Champs-Élysées : parallaxe + t-shirt qui se redresse ---------- */
  const scene = $('.dr-scene');
  if (scene) {
    gsap.fromTo($('.dr-scene__bg', scene), { yPercent: -8, scale: 1.16 }, { yPercent: 8, scale: 1.16, ease: 'none', scrollTrigger: { trigger: scene, start: 'top bottom', end: 'bottom top', scrub: true } });
    gsap.fromTo($('.dr-scene__tee', scene), { y: 160, rotation: 9 }, { y: -30, rotation: -4, ease: 'none', scrollTrigger: { trigger: scene, start: 'top 95%', end: 'bottom 30%', scrub: 0.6 } });
    splitReveal($('.dr-scene__copy h2', scene), 'lines', { stagger: 0.1 }, { trigger: scene, start: 'top 70%' });
    gsap.from($$('.dr-scene__copy > p, .dr-scene__copy .dr-btn', scene), { y: 30, opacity: 0, duration: 1, ease: EASE, stagger: 0.1, scrollTrigger: { trigger: scene, start: 'top 60%' } });
  }

  /* ---------- Infos, journal ---------- */
  $$('.dr-facts').forEach((f) => gsap.from(f.children, { y: 40, opacity: 0, duration: 0.9, ease: EASE, stagger: 0.08, scrollTrigger: { trigger: f, start: 'top 90%' } }));
  const journal = $('.dr-journal__list');
  if (journal) gsap.from(journal.children, { y: 70, opacity: 0, duration: 1.1, ease: EASE, stagger: 0.12, scrollTrigger: { trigger: journal, start: 'top 85%' } });

  /* ---------- Newsletter : parallaxe de la photo ---------- */
  const news = $('.dr-news');
  if (news) {
    gsap.fromTo($('.dr-news__bg', news), { yPercent: -10, scale: 1.18 }, { yPercent: 10, scale: 1.18, ease: 'none', scrollTrigger: { trigger: news, start: 'top bottom', end: 'bottom top', scrub: true } });
    splitReveal($('.dr-news__inner h2', news), 'lines', { stagger: 0.1 }, { trigger: news, start: 'top 70%' });
    gsap.from($$('.dr-news__inner > p, .dr-news__form', news), { y: 30, opacity: 0, duration: 1, ease: EASE, stagger: 0.1, scrollTrigger: { trigger: news, start: 'top 60%' } });
  }

  /* ---------- Fiche produit : titre et photos ---------- */
  const pdpTitle = $('.product-information h1, .product-details h1');
  if (pdpTitle) splitReveal(pdpTitle, 'lines', { duration: 1.1, stagger: 0.08 });
  const gallery = $('.product-information media-gallery, .product-information .product-media-gallery, .product-information [class*="media-gallery"]');
  if (gallery) gsap.from(gallery, { opacity: 0, y: 30, duration: 1.1, ease: EASE });

  /* ---------- Boutons magnétiques (souris uniquement) ---------- */
  if (fine) {
    $$('.dr-btn').forEach((btn) => {
      const xTo = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3' });
      const yTo = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3' });
      btn.addEventListener('pointermove', (e) => {
        const r = btn.getBoundingClientRect();
        xTo((e.clientX - r.left - r.width / 2) * 0.25);
        yTo((e.clientY - r.top - r.height / 2) * 0.35);
      });
      btn.addEventListener('pointerleave', () => { xTo(0); yTo(0); });
    });
  }

  release();
  window.addEventListener('load', () => ScrollTrigger.refresh());

  /* ---------- Filet de sécurité : si les animations ne tournent jamais alors que la page est visible,
     on affiche tout tel quel (navigateurs intégrés qui bloquent requestAnimationFrame, etc.). ---------- */
  const startFrame = gsap.ticker.frame;
  const failSafe = () => {
    if (document.visibilityState !== 'visible') return;
    if (gsap.ticker.frame - startFrame > 5) return;
    ScrollTrigger.getAll().forEach((t) => t.kill());
    gsap.globalTimeline.clear();
    gsap.set('.dr *, .dr-grid > li, .product-information h1, .product-information [class*="media-gallery"]', { clearProps: 'opacity,visibility,transform,clipPath' });
  };
  setTimeout(failSafe, 5000);
})();
